import { t } from "../../../data/strings";
import { C, cardDef, roleStats } from "../sim/accessors";
import type { IwCardId, IwRoleId, IwTeam } from "../sim/types";

/** Combat numbers resolved from catalog shapes / role / deploy SSOT (no magic numbers). */
export type IwCardCombatNums = {
  damage?: number;
  attack?: number;
  hp?: number;
  count?: number;
  radius?: number;
  range?: number;
  durationSec?: number;
  deployDamage?: number;
  deployHeal?: number;
  deployBuff?: number;
  deployBlightSec?: number;
  deployRadius?: number;
};

export type IwCardInfo = {
  id: IwCardId;
  team: IwTeam;
  labelKey: string;
  descKey: string;
  cost: number;
  kind: string;
  combat: IwCardCombatNums;
};

type DeployCatalog = {
  kind?: string;
  damage?: number;
  heal?: number;
  healPerUnit?: number;
  buffMul?: number;
  blightSec?: number;
  radius?: number;
  radiusBonusPerUnit?: number;
};

function readDeploy(def: ReturnType<typeof cardDef>): DeployCatalog | null {
  if (!("deploy" in def)) return null;
  const raw = (def as { deploy?: unknown }).deploy;
  if (!raw || typeof raw !== "object") return null;
  return raw as DeployCatalog;
}

function applyDeployNums(
  out: IwCardCombatNums,
  deploy: DeployCatalog,
  count: number,
): void {
  if (deploy.damage != null) out.deployDamage = deploy.damage;
  const heal =
    deploy.healPerUnit != null ? deploy.healPerUnit * count : deploy.heal;
  if (heal != null) out.deployHeal = heal;
  if (deploy.buffMul != null) out.deployBuff = deploy.buffMul;
  if (deploy.blightSec != null) out.deployBlightSec = deploy.blightSec;
  if (deploy.radius != null) {
    out.deployRadius =
      deploy.radius + (deploy.radiusBonusPerUnit ?? 0) * Math.max(0, count - 1);
  }
}

export function resolveCardCombat(card: IwCardId): IwCardCombatNums {
  const def = cardDef(card);
  if (def.kind === "summon" && "roleId" in def) {
    const role = roleStats(def.roleId as IwRoleId);
    const count =
      "count" in def && typeof def.count === "number" ? def.count : 1;
    const out: IwCardCombatNums = {
      count,
      hp: role.hp,
      attack: role.damage,
    };
    const deploy = readDeploy(def);
    if (deploy) applyDeployNums(out, deploy, count);
    return out;
  }
  const shape = C.shapes[def.kind as keyof typeof C.shapes] as
    | {
        damage?: number;
        radius?: number;
        range?: number;
        delaySec?: number;
        blightSec?: number;
      }
    | undefined;
  if (!shape) return {};
  const out: IwCardCombatNums = {};
  if (shape.damage != null) out.damage = shape.damage;
  if (shape.radius != null) out.radius = shape.radius;
  if (shape.range != null) out.range = shape.range;
  if (shape.delaySec != null) out.durationSec = shape.delaySec;
  if (shape.blightSec != null) out.durationSec = shape.blightSec;
  return out;
}

export function cardInfo(card: IwCardId): IwCardInfo {
  const def = cardDef(card);
  const descKey =
    "descKey" in def && typeof (def as { descKey?: unknown }).descKey === "string"
      ? (def as { descKey: string }).descKey
      : def.labelKey;
  return {
    id: card,
    team: def.team as IwTeam,
    labelKey: def.labelKey,
    descKey,
    cost: def.cost,
    kind: def.kind,
    combat: resolveCardCombat(card),
  };
}

export function hasDeployStats(combat: IwCardCombatNums): boolean {
  return (
    combat.deployDamage != null ||
    combat.deployHeal != null ||
    combat.deployBuff != null ||
    combat.deployBlightSec != null ||
    combat.deployRadius != null
  );
}

/** Full detail stats line (deploy first when present). */
export function formatCardCombatLine(combat: IwCardCombatNums): string {
  const parts: string[] = [];
  if (combat.count != null) parts.push(t("iw.card.stat.count", { n: combat.count }));
  if (combat.deployDamage != null) {
    parts.push(t("iw.card.stat.deploy", { n: combat.deployDamage }));
  }
  if (combat.deployHeal != null) parts.push(t("iw.card.stat.heal", { n: combat.deployHeal }));
  if (combat.deployBuff != null) {
    parts.push(t("iw.card.stat.buff", { n: combat.deployBuff }));
  }
  if (combat.deployBlightSec != null) {
    parts.push(t("iw.card.stat.blight", { n: combat.deployBlightSec }));
  }
  if (combat.deployRadius != null) {
    parts.push(t("iw.card.stat.radius", { n: Math.round(combat.deployRadius) }));
  }
  if (combat.attack != null) parts.push(t("iw.card.stat.atk", { n: combat.attack }));
  if (combat.hp != null) parts.push(t("iw.card.stat.hp", { n: combat.hp }));
  if (combat.damage != null) parts.push(t("iw.card.stat.dmg", { n: combat.damage }));
  if (combat.radius != null) parts.push(t("iw.card.stat.radius", { n: combat.radius }));
  if (combat.range != null) parts.push(t("iw.card.stat.range", { n: combat.range }));
  if (combat.durationSec != null) {
    parts.push(t("iw.card.stat.duration", { n: combat.durationSec }));
  }
  return parts.join(" · ");
}

/** Short face line — deploy numbers take priority over basic AA. */
export function formatCardFaceStats(combat: IwCardCombatNums): string {
  const parts: string[] = [];
  if (combat.count != null) parts.push(t("iw.card.stat.count", { n: combat.count }));
  if (combat.deployDamage != null) {
    parts.push(t("iw.card.stat.deploy", { n: combat.deployDamage }));
  } else if (combat.deployHeal != null) {
    parts.push(t("iw.card.stat.heal", { n: combat.deployHeal }));
  } else if (combat.deployBlightSec != null) {
    parts.push(t("iw.card.stat.blight", { n: combat.deployBlightSec }));
  } else if (combat.damage != null) {
    parts.push(t("iw.card.stat.dmg", { n: combat.damage }));
  } else if (combat.attack != null) {
    parts.push(t("iw.card.stat.atk", { n: combat.attack }));
  }
  if (combat.deployBuff != null && combat.deployDamage == null && combat.deployHeal == null) {
    parts.push(t("iw.card.stat.buff", { n: combat.deployBuff }));
  }
  if (combat.deployRadius != null) {
    parts.push(t("iw.card.stat.radius", { n: Math.round(combat.deployRadius) }));
  } else if (combat.radius != null) {
    parts.push(t("iw.card.stat.radius", { n: Math.round(combat.radius) }));
  } else if (combat.range != null) {
    parts.push(t("iw.card.stat.range", { n: Math.round(combat.range) }));
  }
  if (combat.durationSec != null) {
    parts.push(t("iw.card.stat.duration", { n: combat.durationSec }));
  }
  return parts.join(" · ");
}
